import { useContext } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import {
  PageContext,
  SiteSettingsContext,
  useConfig,
  useGeneralRoutes
} from '@namespace/cms';
import { useIsNavMenuVisible } from '@namespace/common';
import { LocalizedLink } from '@namespace/i18n';
import { useColorScheme } from '@namespace/themes';
import { USER_ROLES, UserContext } from '@namespace/user';
import { HEADER_COLOR_SCHEME } from '../../constants';
import LogoComponent from './common/LogoComponent';

import styles from './index.module.css';

export const Logo = ({
  id,
  imgClassName = '',
  className = '',
  isProfileMenu = false
}) => {
  const { config } = useConfig(id);
  const {
    home = '',
    lobby = '',
    logoLinkForUserWithNavMenu = ''
  } = useGeneralRoutes();
  const isNavMenuVisible = useIsNavMenuVisible();
  const [user] = useContext(UserContext);
  const [settings] = useContext(SiteSettingsContext);
  const { page } = useContext(PageContext);
  const { pageConfig = {} } = page;
  const { headerColorScheme } = pageConfig;
  const { isDarkModeEnabled } = useColorScheme();

  const { role } = user;

  const isGuest = role === USER_ROLES.GUEST;

  const {
    siteLogo = '',
    siteLogoSecondary = '/images/logo/logoSecondary.svg',
    logoSmall,
    logoSmallSecondary = '/images/logo/logoSmallSecondary.svg',
    darkSiteLogo = '',
    darkSiteLogoSecondary = '',
    darkLogoSmall = '',
    darkLogoSmallSecondary = ''
  } = !isEmpty(config) ? config : settings?.siteConfig;

  const { basicLogo, secondaryLogo, basicLogoSmall, secondaryLogoSmall } = {
    basicLogo: siteLogo,
    secondaryLogo: siteLogoSecondary,
    basicLogoSmall: logoSmall,
    secondaryLogoSmall: logoSmallSecondary
  };

  const {
    basicLogoDark,
    secondaryLogoDark,
    basicLogoSmallDark,
    secondaryLogoSmallDark
  } = {
    basicLogoDark: darkSiteLogo || siteLogo,
    secondaryLogoDark: darkSiteLogoSecondary || siteLogo,
    basicLogoSmallDark: darkLogoSmall || logoSmall,
    secondaryLogoSmallDark: darkLogoSmallSecondary || logoSmall
  };

  const IS_SECONDARY_COLOR_SCHEME = !isProfileMenu
    ? headerColorScheme === HEADER_COLOR_SCHEME.SECONDARY
    : false;

  const classes = [
    imgClassName,
    headerColorScheme,
    styles[
      IS_SECONDARY_COLOR_SCHEME
        ? HEADER_COLOR_SCHEME.SECONDARY
        : HEADER_COLOR_SCHEME.PRIMARY
    ]
  ];
  const mobileMedia = '(max-width: 479px)';
  const desktopMedia = '(min-width: 480px)';

  const linkTo = () => {
    if (isGuest) {
      return lobby;
    }
    if (isNavMenuVisible) {
      return logoLinkForUserWithNavMenu;
    }
    return home;
  };

  return (
    <LocalizedLink
      onClick={() => logAction(LOG_ACTIONS.LOGO_HEADER_CLICK)}
      to={linkTo()}
      className={`${styles.container} ${className}`}
    >
      {(!isDarkModeEnabled || window.IS_SSR) && (
        <>
          <LogoComponent
            src={
              IS_SECONDARY_COLOR_SCHEME ? secondaryLogoSmall : basicLogoSmall
            }
            media={mobileMedia}
            className={clsx(styles.imgClassName, styles.logoMobile, ...classes)}
          />
          <LogoComponent
            src={IS_SECONDARY_COLOR_SCHEME ? secondaryLogo : basicLogo}
            media={desktopMedia}
            className={clsx(
              styles.imgClassName,
              styles.logoDesktop,
              ...classes
            )}
          />
        </>
      )}
      {(isDarkModeEnabled || window.IS_SSR) && (
        <>
          <LogoComponent
            src={
              IS_SECONDARY_COLOR_SCHEME
                ? secondaryLogoSmallDark
                : basicLogoSmallDark
            }
            media={mobileMedia}
            className={clsx(
              styles.imgDarkClassName,
              styles.logoMobile,
              ...classes
            )}
          />
          <LogoComponent
            src={IS_SECONDARY_COLOR_SCHEME ? secondaryLogoDark : basicLogoDark}
            media={desktopMedia}
            className={clsx(
              styles.imgDarkClassName,
              styles.logoDesktop,
              ...classes
            )}
          />
        </>
      )}
    </LocalizedLink>
  );
};
